import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";
import { Globals } from "@coreapp/CoreApp/Globals";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useNativeGuides } from "./NativeGuidesProvider";
import { guideRegistry } from "../guides/guideRegistry";
import type { GuideDefinition, GuideGateArgs } from "../NativeGuideTypes";

const GuideOrchestrator: React.FC = () => {
  const { isLoaded, shouldShowGuide, dismissGuide, completeGuide } = useNativeGuides();
  const permissions = usePermissionCheck();
  const { pathname } = useLocation();
  const [activeGuideId, setActiveGuideId] = useState<string | null>(null);

  const onboardingNeeded = !!Globals._wsMgr?.GetOrgInfo?.()?.GetOnboardingNeeded?.();
  const gateArgs: GuideGateArgs = { brand: gWhiteLabel, permissions, pathname, onboardingNeeded };
  const isEligible = (g: GuideDefinition) => shouldShowGuide(g) && (!g.gate || g.gate(gateArgs));

  useEffect(() => {
    if (!isLoaded) return;
    const first = Object.values(guideRegistry).find(isEligible);
    setActiveGuideId(first?.id ?? null);
  }, [isLoaded, shouldShowGuide, gateArgs.brand, gateArgs.pathname, permissions]);

  if (!isLoaded || !activeGuideId) return null;

  const guide = guideRegistry[activeGuideId];
  if (!guide) return null;

  const handleDismiss = () => {
    dismissGuide(guide.id);
    setActiveGuideId(null);
  };

  const handleComplete = () => {
    completeGuide(guide.id);
    if (guide.next) {
      const nextGuide = guideRegistry[guide.next];
      setActiveGuideId(nextGuide && isEligible(nextGuide) ? guide.next : null);
    } else {
      setActiveGuideId(null);
    }
  };

  const { Component } = guide;
  return <Component guideId={guide.id} onComplete={handleComplete} onDismiss={handleDismiss} />;
};

export default GuideOrchestrator;
